"use client";

import { useEffect } from "react";

/**
 * Applies a resolved brand's `colorTokens` onto the `--brand-*` custom
 * properties `theme.css` reads from (`03-data-model.md` § 10.4). This is the
 * "injected per request" half of white-labelling that nothing used to do —
 * see `apps/api/src/lib/brand.ts` for how the tokens are resolved.
 */

export type BrandTokenRoot = {
  style: {
    setProperty(property: string, value: string): void;
    removeProperty(property: string): void;
  };
};

const UNSAFE_VALUE = [";", "{", "}", "url("];

/**
 * Sets each `--brand-*` entry in `tokens` on `root` and returns a cleanup
 * that removes exactly the properties it set.
 *
 * Only keys starting with `--brand-` are honoured — a brand row cannot
 * restyle anything outside the brand layer — and a value carrying `;`, `{`,
 * `}` or `url(` is skipped: a colour token is a colour, and an inline style
 * must not become an injection point.
 */
export function applyBrandTokens(
  tokens: Record<string, string>,
  root: BrandTokenRoot = document.documentElement,
): () => void {
  const applied: string[] = [];

  for (const [property, value] of Object.entries(tokens)) {
    if (!property.startsWith("--brand-")) continue;
    if (UNSAFE_VALUE.some((token) => value.includes(token))) continue;
    root.style.setProperty(property, value);
    applied.push(property);
  }

  return () => {
    for (const property of applied) root.style.removeProperty(property);
  };
}

/**
 * Applies `brand.colorTokens` for as long as the caller is mounted, and
 * removes them again the moment `brand` changes or it unmounts — a store
 * switch that changes the brand then leaves no stale colour behind.
 */
export function useBrandTokens(brand: { colorTokens: Record<string, string> } | undefined): void {
  useEffect(() => {
    if (!brand) return;
    return applyBrandTokens(brand.colorTokens);
  }, [brand]);
}
